/* eslint-disable parog-web/no-glint-expect-error-directive */
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import dayjs from 'dayjs';
import type { EmberChangeset } from 'ember-changeset';
import {
  validateNumber,
  validatePresence
} from 'ember-changeset-validations/validators';
import type { IntlService } from 'ember-intl';
import t from 'ember-intl/helpers/t';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import JournalEventBasedComponent from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import capitalize from 'parog-web/helpers/capitalize';
import ThymicEpisodeOccurredJournalEvent from 'parog-web/models/thymic-episode-occurred-journal-event';

interface Args extends ForcBaseArgs {
  onChangesetCreated: (changeset: EmberChangeset) => void;
  onChangesetDestroyed: (changeset: EmberChangeset) => void;
  shouldForceQuit: boolean;
  model: Record<string, unknown>;
}

/* eslint-disable parog-web/migrate-non-patch-forms */
export default class ThymicEpisodeForm extends JournalEventBasedComponent<Args> {
  static override forcDisplayName = 'Épisode thymique';
  static override fullComponentId = 'thymic-episode/edition-form';
  static override journalEventClass = ThymicEpisodeOccurredJournalEvent;

  @service declare intl: IntlService;

  override journalEventProperties = [
    'occurredAt',
    'episodeType',
    'episodeSeverity',
    'withAnxiousDistress',
    'withMixedCharacteristics',
    'withPsychoticCongruentWithMood',
    'withPsychoticNotCongruentWithMood',
    'withCatatonia',
    'withMelancholicCharacteristics',
    'wihOnsetDuringPeripartum',
    'withAtypicalCharacteristics',
    'episodeDuration',
    'hospitalization',
    'tsPresence',
    'commentary'
  ];
  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;

  get isDepressiveEpisode() {
    return this.changeset.get('episodeType') === 'depressive';
  }

  get patientAgeAtTheTimeOfTheEpisode() {
    const episodeDate = this.changeset.get('occurredAt');
    const birthday = this.args.patient.birthday;

    return episodeDate
      ? dayjs(episodeDate).diff(dayjs(birthday), 'years')
      : null;
  }

  override get validations() {
    // FIXME add the required validatiosn in the yml + change the type of episodeDuration
    const validations = this.journalEventClass.validations(this.intl);

    validations['hospitalization'] = validatePresence({
      presence: true,
      description: "L'hospitalisation"
    });
    validations['tsPresence'] = validatePresence({
      presence: true,
      description: "La présence d'une TS"
    });
    validations['episodeDuration'] = validateNumber({
      integer: true,
      positive: true,
      allowBlank: true,
      description: "La durée de l'épisode en semaine"
    });
    return validations;
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);
    if (this.args.onChangesetCreated) {
      this.args.onChangesetCreated(this.changeset);
    }
    this.shouldForceQuit = this.args.shouldForceQuit || false;
  }

  override retrieveModel() {
    return this.args.model || super.retrieveModel();
  }

  override willDestroy() {
    if (this.args.onChangesetDestroyed) {
      this.args.onChangesetDestroyed(this.changeset);
    }
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <div class='fields'>
          <div class='eight wide field'>
            <Form.Date
              @propertyName='occurredAt'
              @labelName="Date de l'épisode"
              @isMandatory={{true}}
            />
          </div>
          <div class='eight wide field'>
            {{#if this.patientAgeAtTheTimeOfTheEpisode}}
              <div
                class='placeholder-label pt-[2.5rem]'
                {{! @glint-expect-error }}
                data-test-patient-age={{@index}}
              >
                Le patient avait
                {{this.patientAgeAtTheTimeOfTheEpisode}}
                ans au moment de l'épisode.
              </div>
            {{/if}}
          </div>
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title="Type d'épisode"
            @propertyName='episodeType'
            @isMandatory={{true}}
            as |Group|
          >
            <Group.FieldRadio
              @value='depressive'
              @label={{capitalize (t 'thymic-episode.episodeType.depressive')}}
            />
            <Group.FieldRadio
              @value='hypomania'
              @label={{capitalize (t 'thymic-episode.episodeType.hypomania')}}
            />
            <Group.FieldRadio
              @value='mania'
              @label={{capitalize (t 'thymic-episode.episodeType.mania')}}
            />
            <Group.FieldRadio
              @value='unspecified'
              @label={{capitalize (t 'thymic-episode.episodeType.unspecified')}}
            />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title="Sévérité de l'épisode"
            @propertyName='episodeSeverity'
            as |Group|
          >
            <Group.FieldRadio
              @value='light'
              @label={{capitalize (t 'thymic-episode.episodeSeverity.light')}}
            />

            <Group.FieldRadio
              @value='medium'
              @label={{capitalize (t 'thymic-episode.episodeSeverity.medium')}}
            />
            <Group.FieldRadio
              @value='severe'
              @label={{capitalize (t 'thymic-episode.episodeSeverity.severe')}}
            />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='field'>
          <div class='field'>
            <label>Présence d'une ou plusieurs des caractéristiques suivantes</label>
          </div>
          <div class='field'>
            <Form.Checkbox
              @labelName='Avec détresse anxieuse'
              @payloadKey='withAnxiousDistress'
            />
          </div>
          <div class='field'>
            <Form.Checkbox
              @labelName='Avec caractéristiques mixtes'
              @payloadKey='withMixedCharacteristics'
            />
          </div>
          <div class='field'>
            <Form.Checkbox
              @labelName="Avec caractéristiques psychotiques congruentes à l'humeur"
              @payloadKey='withPsychoticCongruentWithMood'
            />
          </div>
          <div class='field'>
            <Form.Checkbox
              @labelName="Avec caractéristiques psychotiques non congruentes à l'humeur"
              @payloadKey='withPsychoticNotCongruentWithMood'
            />
          </div>
          <div class='field'>
            <Form.Checkbox
              @labelName='Avec catatonie'
              @payloadKey='withCatatonia'
            />
          </div>
          <div class='field'>
            <Form.Checkbox
              @labelName='Avec début lors du péripartum'
              @payloadKey='withOnsetDuringPeripartum'
            />
          </div>
          {{#if this.isDepressiveEpisode}}
            <div class='field'>
              <Form.Checkbox
                @labelName='Avec caractéristiques mélancoliques'
                @payloadKey='withMelancholicCharacteristics'
              />
            </div>
            <div class='field'>
              <Form.Checkbox
                @labelName='Avec caractéristiques atypiques'
                @payloadKey='withAtypicalCharacteristics'
              />
            </div>
          {{/if}}
        </div>

        <div class='field'>
          <Form.NumberInput
            @labelName="Durée de l'épisode en semaines"
            @propertyName='episodeDuration'
            @type='integer'
            @min='0'
          />
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @propertyName='hospitalization'
            @isMandatory={{true}}
            @title='Hospitalisation'
            as |Group|
          >
            <Group.FieldRadio @label='Oui' @value={{true}} />
            <Group.FieldRadio @label='Non' @value={{false}} />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @propertyName='tsPresence'
            @isMandatory={{true}}
            @title="Présence d'une TS"
            as |Group|
          >
            <Group.FieldRadio @label='Oui' @value={{true}} />
            <Group.FieldRadio @label='Non' @value={{false}} />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='field'>
          <Form.TextArea
            @labelName='Précisions'
            @propertyName='commentary'
            @rows={{2}}
          />
        </div>
      </:content>
      <:footer as |Footer|>
        {{! @glint-expect-error }}
        {{#unless @hideFooter}}
          <Footer.CancelButton />
          <Footer.SubmitButton />
        {{/unless}}
      </:footer>
    </Provider>
  </template>
}
